import { inviteCodeQueryString } from '@/providers/invite-code';
import {
    EnumFeature,
    EnumScreenName,
    EnumTheme,
    EventProperties,
    EventScreenView,
} from '@calm/evexia-analytics';
import { logger } from '@rhg/libs/logger';
import { ftueQueryString } from '@rhg/utils/constants';
import { routes } from '@rhg/utils/routes';
import { ParsedUrlQuery } from 'querystring';
import { AnalyticsDestination } from '../destination/types';
import mapBaseEventProps from '../map-base-event-props';
import { analyticsRouteMap } from './types';

interface TrackAsPageNavProps {
    screenName: EnumScreenName;
    feature: EnumFeature;
    themeName: EnumTheme;
    destinations: AnalyticsDestination[];
    query?: ParsedUrlQuery;
    additionalProps?: EventProperties;
}

interface TrackPageNavProps {
    url: string;
    themeName: EnumTheme;
    destinations: AnalyticsDestination[];
    query?: ParsedUrlQuery;
    isAuthenticated: boolean;
}

/** Registration screens determine this on their own via the current RegistrationFlow */
const preRegistrationScreenNameWithIsDependent = [
    EnumScreenName.Landing,
    EnumScreenName.LoginRegistration,
    EnumScreenName.Signup,
];

/**
 * Returns a mapping of the desired analytics properties provided by
 * the page's query params.
 * @param query The URL's query map
 */
const getQueryAdditionalAnalyticsProperties = (
    screenName: EnumScreenName,
    query?: ParsedUrlQuery
): EventProperties => {
    const onboardingQuery = query?.[ftueQueryString];
    const inviteQuery = query?.[inviteCodeQueryString];
    return {
        ...(onboardingQuery
            ? { IsOnboarding: (onboardingQuery === 'true').toString() }
            : {}),
        ...(preRegistrationScreenNameWithIsDependent.includes(screenName)
            ? { IsDependent: Boolean(inviteQuery) }
            : {}),
    };
};

/**
 * Returns the analytics values for a given route
 * @param url
 * @returns [EnumScreenName, EnumFeature]
 * @example
 * getAnalyticsForRouteAndQuery('/screening') // [EnumScreenName.FtueSurveyStart, EnumFeature.FtueSurvey]
 **/
function getAnalyticsForRoute(url: string): [EnumScreenName, EnumFeature] {
    return (
        analyticsRouteMap[url] ?? [
            EnumScreenName.NotMapped,
            EnumFeature.NotMapped,
        ]
    );
}

export function getScreenName(url: string, isAuthenticated: boolean) {
    if (isAuthenticated && url === routes.dashboard) {
        return EnumScreenName.Home;
    }

    return getAnalyticsForRoute(url)?.[0];
}

export function getFeatureName(url: string) {
    return getAnalyticsForRoute(url)?.[1];
}

/**
 * Tracks a page nav event that isn't tied to a specific route
 * @param screenName
 */
export async function trackAsPageNav({
    screenName,
    feature,
    themeName,
    query,
    destinations,
    additionalProps,
}: TrackAsPageNavProps) {
    const properties: EventScreenView = {
        ...additionalProps,
        IsScreenView: true,
        ScreenName: screenName,
        Feature: feature,
        Theme: themeName,
    };

    await Promise.all(
        destinations.map((dest) =>
            dest.track(
                'Screen : Viewed',
                mapBaseEventProps({
                    ...getQueryAdditionalAnalyticsProperties(screenName, query),
                    ...properties,
                })
            )
        )
    );
}

export default async function trackPageNav({
    url,
    query,
    themeName,
    destinations,
    isAuthenticated,
}: TrackPageNavProps) {
    // The auth route is a transient redirection point
    // don't log it as its own page view
    if (url === routes.auth) {
        return;
    }

    const screenName = getScreenName(url, isAuthenticated);
    const featureName = getFeatureName(url);

    if (screenName === EnumScreenName.NotMapped) {
        logger.warn('Missing event name for path', { path: url });
        await Promise.all(
            destinations.map((dest) =>
                dest.track('UnmappedPageNav', mapBaseEventProps({}))
            )
        );
        return;
    }

    const properties: EventScreenView = {
        IsScreenView: true,
        ScreenName: screenName,
        Feature: featureName ?? EnumFeature.NotMapped,
        Theme: themeName,
    };

    await Promise.all(
        destinations.map((dest) =>
            dest.track(
                'Screen : Viewed',
                mapBaseEventProps({
                    ...getQueryAdditionalAnalyticsProperties(screenName, query),
                    ...properties,
                })
            )
        )
    );
}
